import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";

// Single source of truth for supported locales, in canonical (display) order.
export const SYSTEM_LOCALES: ReadonlyArray<{ iso: LanguagesIsoEnum; label: string; endonym: string }> = [
  { iso: LanguagesIsoEnum.enUS, label: "English", endonym: "English" },
  { iso: LanguagesIsoEnum.esUS, label: "Spanish", endonym: "Español" },
];

const LOCALE_DISPLAY_NAMES: Record<string, string> = SYSTEM_LOCALES.reduce<Record<string, string>>(
  (acc, { iso, label }) => {
    acc[iso] = label;
    return acc;
  },
  {},
);

export function getLocaleDisplayName(locale: string | null | undefined): string | null {
  if (!locale) {
    return null;
  }
  return LOCALE_DISPLAY_NAMES[locale] ?? locale;
}

export const ALL_SYSTEM_LOCALES: LanguagesIsoEnum[] = SYSTEM_LOCALES.map(({ iso }) => iso);

// Admin-facing warning listing the languages a test element can be saved with. Derived from the
// enum rather than hardcoded, so adding or renaming a language can't leave the message stale. Lists
// the tags rather than the display names because those are the exact keys the admin is editing.
export const SUPPORTED_LANGUAGES_MESSAGE = `Supported languages are: ${ALL_SYSTEM_LOCALES.map(
  (locale) => `"${locale}"`,
).join(", ")}`;

// The locale we fall back to when none is selected. English today; named for the role, not the language.
export const DEFAULT_LOCALE: LanguagesIsoEnum = LanguagesIsoEnum.enUS;

export const localeLabel = (locale: string): string => getLocaleDisplayName(locale) ?? locale;

const LOCALE_ENDONYMS: Record<string, string> = SYSTEM_LOCALES.reduce<Record<string, string>>(
  (acc, { iso, endonym }) => {
    acc[iso] = endonym;
    return acc;
  },
  {},
);

/**
 * The name of a language written in that language. Kept separate from localeLabel, which names
 * every language in English.
 *
 * @param {string} locale - locale tag to name
 * @returns {string} the language's own name, falling back to the raw tag
 */
export const localeEndonym = (locale: string): string => LOCALE_ENDONYMS[locale] ?? locale;

// Canonical (English-first) order; unknown locales dropped.
export const orderLocales = (locales: string[]): string[] =>
  ALL_SYSTEM_LOCALES.filter((locale) => locales.includes(locale));

// Resolves the first non-empty source to canonical order, falling back to the default locale —
// keeps the selector, tabs, and save payload in agreement.
export const resolveEffectiveLocales = (...localeSources: Array<string[] | undefined>): string[] =>
  orderLocales(localeSources.find((source) => source?.length) ?? [DEFAULT_LOCALE]);
